import { useState } from "react";
import { Link } from "react-router-dom";
import { createProject } from "../lib/projects";
import { sendProjectNotification } from "../lib/telegram";
import { useAuth } from "../lib/auth";
import { isValidPhone } from "../lib/profiles";
import { Spinner } from "../components/ui/Loader";
import { LuCircleCheck } from "react-icons/lu";

export const SubmitProject = () => {
  const { user } = useAuth();
  const [clientName, setClientName] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [area, setArea] = useState("");
  const [comment, setComment] = useState("");
  const [step, setStep] = useState<1 | 2>(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);

  const handleFirstStep = (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (!clientName.trim()) {
      setError("Вкажіть імʼя клієнта");
      return;
    }
    if (!isValidPhone(clientPhone)) {
      setError("Вкажіть коректний телефон клієнта");
      return;
    }
    setStep(2);
  };

  const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError("Вкажіть назву проєкту");
      return;
    }
    const areaNum = Number(area.replace(",", "."));
    if (!area || Number.isNaN(areaNum) || areaNum <= 0) {
      setError("Вкажіть додатну площу в м²");
      return;
    }
    setSubmitting(true);
    try {
      const project = await createProject({
        name: name.trim(),
        clientName: clientName.trim(),
        clientPhone: clientPhone.trim(),
        address: address.trim(),
        areaM2: areaNum,
        comment: comment.trim(),
      });
      setCreatedId(project.project_code ?? project.id);
      sendProjectNotification({
        event: "new_project",
        project_id: project.id,
        project_name: project.name,
        project_code: project.project_code,
        owner_id: user!.id,
      }).catch(() => undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Помилка збереження проєкту");
    } finally {
      setSubmitting(false);
    }
  };

  if (createdId) {
    return (
      <main className="flex flex-1 items-center justify-center px-5 py-12">
        <div className="card w-full max-w-md p-8 text-center">
          <LuCircleCheck className="mx-auto text-5xl text-green-400" aria-hidden />
          <h1 className="mt-4 text-2xl font-bold text-white">Проєкт передано</h1>
          <p className="mt-2 text-sm text-neutral-400">
            Номер проєкту: <span className="font-bold text-brand">{createdId}</span>
          </p>
          <p className="mt-1 text-sm text-neutral-400">Статус: Новий</p>
          <div className="mt-6 flex flex-col gap-3">
            <Link to="/projects" className="btn-brand justify-center">
              Мої проєкти
            </Link>
            <button
              type="button"
              onClick={() => {
                setCreatedId(null);
                setClientName("");
                setClientPhone("");
                setName("");
                setAddress("");
                setArea("");
                setComment("");
              }}
              className="btn-outline-brand justify-center"
            >
              Передати ще один
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-1 px-5 py-8 md:px-8">
      <h1 className="text-2xl font-bold text-white">Передати новий проєкт</h1>
          {step === 1 ? (
            <form onSubmit={handleFirstStep} className="mt-7 flex max-w-xl flex-col gap-4">
              <fieldset className="card p-5">
                <legend className="px-2 text-sm font-bold uppercase tracking-wider text-neutral-400">
                  Дані клієнта
                </legend>
                <div className="flex flex-col gap-4">
                  <label className="block">
                    <span className="mb-2 block text-sm font-semibold text-neutral-300">
                      Імʼя клієнта
                    </span>
                    <input
                      className="input-dark"
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      required
                    />
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-sm font-semibold text-neutral-300">
                      Телефон клієнта
                    </span>
                    <input
                      className="input-dark"
                      type="tel"
                      placeholder="+380..."
                      value={clientPhone}
                      onChange={(e) => setClientPhone(e.target.value)}
                      required
                    />
                  </label>
                </div>
              </fieldset>

              {error && (
                <p className="rounded border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
                  {error}
                </p>
              )}
              <button type="submit" className="mt-3 btn-brand w-full justify-center">
                Далі
              </button>
            </form>
          ) : (
            <form onSubmit={handleSubmit} className="mt-7 flex max-w-xl flex-col gap-4">
              <fieldset className="card p-5">
                <legend className="px-2 text-sm font-bold uppercase tracking-wider text-neutral-400">
                  Дані обʼєкта
                </legend>
                <div className="flex flex-col gap-4">
                  <label className="block">
                    <span className="mb-2 block text-sm font-semibold text-neutral-300">
                      Назва проєкту / обʼєкта
                    </span>
                    <input
                      className="input-dark"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-sm font-semibold text-neutral-300">
                      Адреса або населений пункт
                    </span>
                    <input
                      className="input-dark"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                    />
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-sm font-semibold text-neutral-300">
                      Площа, м²
                    </span>
                    <input
                      className="input-dark"
                      type="number"
                      min="1"
                      step="0.01"
                      inputMode="decimal"
                      value={area}
                      onChange={(e) => setArea(e.target.value)}
                      required
                    />
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-sm font-semibold text-neutral-300">
                      Коментар архітектора
                    </span>
                    <textarea
                      className="input-dark"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                    />
                  </label>
                </div>
              </fieldset>

              {error && (
                <p className="rounded border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="mt-3 btn-brand w-full justify-center disabled:opacity-70"
              >
                {submitting ? (
                  <>
                    <Spinner className="h-4 w-4" />
                    Передача…
                  </>
                ) : (
                  "Передати проєкт"
                )}
              </button>
            </form>
          )}
              </main>
            );
          };
